import { useRef, useState, useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "motion/react";
import { IMDSSessionProps, SessionContextMention } from "@/mds/components/sessions/session.types";
import { MDSCombobox, MDSComboboxVariant, MDSComboboxSubmitHandler } from "@/mds/components/forms";
import {
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import { useMDSSession } from "@/mds/components/sessions/session-context/mds-session-context";
import { extractCollectionMentions, extractNoteMentions } from "./session-content.utils";
import { MDSSessionList } from "../session-list";
import { MDSBanner } from "@/mds/components/banner/banner";
import { MDSBannerKind, MDSBannerSlotName } from "@/mds/components/banner/banner.types";
import { useSessionFeature } from "../session-context";
import { SessionFeature, SessionStatus } from "../session.types";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";

// Fallback height when dynamic height hasn't been measured yet
const defaultComboboxHeight = 58;

export const MDSSessionContentStatic = ({
  handlers: { handleFetchComboboxData, handleInputFormSubmit, handleNewSession },
  comboboxProps,
  isOpen = true,
}: IMDSSessionProps) => {
  const {
    sessionViewMode,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    sessionStatus,
    activeArtifact,
    sessionConfig,
    finalizedSessionItems,
    internalSessionEvents,
    setComboboxInputRef,
  } = useMDSSession();

  // Derive sessionId from the first session event
  const sessionId = internalSessionEvents[0]?.session_id || "";

  const isNewChatButtonEnabled = useSessionFeature(
    SessionFeature.ContextLimitExceededNewChatButton
  );

  const comboboxInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const comboboxContainerRef = useRef<HTMLFormElement>(null);
  const bannerRef = useRef<HTMLElement>(null);
  const [comboboxHeight, setComboboxHeight] = useState(defaultComboboxHeight);
  const [bannerHeight, setBannerHeight] = useState(0);

  // Set the combobox input ref in the session context when the component mounts and updates
  useEffect(() => {
    setComboboxInputRef(comboboxInputRef.current);
    // Focus on open once the input is actually mounted — this effect fires when
    // the ref becomes available, so it never races the combobox's own mount. A
    // delay lets a slide-in transform settle first; focusing mid-slide makes
    // the browser scroll the animating container and strand the list partway.
    if (sessionConfig.autoFocusComposer && comboboxInputRef.current) {
      const input = comboboxInputRef.current;
      const focus = () =>
        input.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      const delay = sessionConfig.autoFocusComposerDelayMs ?? 0;
      if (delay <= 0) {
        focus();
        return;
      }
      const timer = setTimeout(focus, delay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboboxInputRef.current]);

  // Track banner height when visible
  const updateBannerHeight = useCallback(() => {
    if (bannerRef.current) {
      const newHeight = bannerRef.current.offsetHeight;
      if (newHeight !== bannerHeight) {
        setBannerHeight(newHeight);
      }
    } else if (bannerHeight !== 0) {
      setBannerHeight(0);
    }
  }, [bannerHeight]);

  useEffect(() => {
    updateBannerHeight();
  });

  useEffect(() => {
    let bannerObserver: ResizeObserver | null = null;
    if (bannerRef.current) {
      bannerObserver = new ResizeObserver(() => updateBannerHeight());
      bannerObserver.observe(bannerRef.current);
    }

    return () => {
      if (bannerObserver) bannerObserver.disconnect();
    };
  }, [updateBannerHeight]);

  // Custom input change handler that includes height tracking
  const handleInputChangeWithHeight = useCallback(
    (height?: number) => {
      // Call the original input change handler
      handleInputChange();

      // record the new height
      if (
        typeof height === "number" &&
        height >= defaultComboboxHeight &&
        height !== comboboxHeight
      ) {
        setComboboxHeight(height);
      }
    },
    [comboboxHeight, handleInputChange]
  );

  const handleComboboxSubmit: MDSComboboxSubmitHandler = ({
    mdxContent,
    mentions,
    implicitMentions,
  }) => {
    // TODO - Clean this up.
    const mentionedCollections = extractCollectionMentions(mentions);
    const mentionedNotes = extractNoteMentions(mentions);

    let extractedImplicitMentions: SessionContextMention[] | undefined;
    if (implicitMentions) {
      const implicitMentionedNoted = extractNoteMentions(implicitMentions);
      const implicitMentionedCollections = extractCollectionMentions(implicitMentions);

      extractedImplicitMentions = [...implicitMentionedCollections, ...implicitMentionedNoted];
    }

    return handleInputFormSubmit({
      content: mdxContent,
      context: {
        // TODO - Clean this up.
        mentions: [...mentionedCollections, ...mentionedNotes],
        implicit_mentions: extractedImplicitMentions,
      },
    });
  };

  const isContextLimitExceeded = sessionStatus === SessionStatus.ContextLimitExceeded;
  // Caller focus handlers compose with the session context's own — spreading
  // them onto the combobox directly would replace it and break isInputFocused.
  const {
    onInputFocus: onCallerInputFocus,
    onInputBlur: onCallerInputBlur,
    ...comboboxOverrides
  } = comboboxProps ?? ({} as NonNullable<IMDSSessionProps["comboboxProps"]>);
  // Everything in the viewport that isn't list or combobox. Embedders with
  // different chrome (e.g. the floating widget window) override it via config.
  const chromeHeightPx = sessionConfig.staticViewChromeHeightPx ?? 176;
  const heightBasis = sessionConfig.staticViewHeightBasis ?? "100dvh";
  const listHeight = `calc(${heightBasis} - ${chromeHeightPx}px - ${comboboxHeight}px${isContextLimitExceeded ? ` - ${bannerHeight}px` : ""})`;

  return (
    <AnimatePresence mode="wait">
      <ContentContainer data-component="ContentContainer">
        <AnimatedContainer>
          {isContextLimitExceeded && (
            <MDSBanner
              kind={MDSBannerKind.Error}
              iconKind={MDSIconKind.InfoCircle}
              message="This chat has gotten too long"
              ref={bannerRef}
            >
              {isNewChatButtonEnabled && (
                <MDSButton
                  variant={MDSButtonVariant.Danger}
                  slot={MDSBannerSlotName.Right}
                  onClick={handleNewSession}
                >
                  New chat
                </MDSButton>
              )}
            </MDSBanner>
          )}

          {isOpen && (
            <StyledListContainer
              data-component="StyledListContainer"
              // The delayed fade smooths chat-to-chat switches; single-chat
              // embedders opt out so the list paints immediately.
              initial={{ opacity: sessionConfig.disableListEnterAnimation ? 1 : 0 }}
              animate={{ opacity: isOpen ? 1 : 0 }}
              transition={
                sessionConfig.disableListEnterAnimation
                  ? { duration: 0 }
                  : { duration: 0.8, delay: 0.5, ease: [0, 0.71, 0.2, 1.01] }
              }
              exit={{ opacity: 0 }}
            >
              <MDSSessionList
                sessionItems={finalizedSessionItems}
                sessionId={sessionId}
                sessionStatus={sessionStatus}
                activeArtifact={activeArtifact}
                containerHeight={listHeight}
                sessionConfig={sessionConfig}
                sessionViewMode={sessionViewMode}
                disableTopFade
              />
            </StyledListContainer>
          )}

          <StyledSection
            data-component="StyledSection"
            $fadeColor={sessionConfig.staticViewComposerFadeColor}
          >
            <StyledForm
              ref={comboboxContainerRef}
              onSubmit={e => {
                e.preventDefault();
              }}
            >
              <MDSCombobox
                placeholder="Anything else?"
                fetchData={handleFetchComboboxData}
                variant={MDSComboboxVariant.Brand}
                onSubmit={handleComboboxSubmit}
                popoverProps={{
                  shouldFlip: true,
                }}
                onInputFocus={() => {
                  handleInputFocus();
                  onCallerInputFocus?.();
                }}
                onInputBlur={() => {
                  handleInputBlur();
                  onCallerInputBlur?.();
                }}
                onInputChange={handleInputChangeWithHeight}
                autoFocus={false}
                inputRef={comboboxInputRef}
                mentions={[]}
                isDisabled={isContextLimitExceeded}
                {...comboboxOverrides}
              />
            </StyledForm>
          </StyledSection>
        </AnimatedContainer>
      </ContentContainer>
    </AnimatePresence>
  );
};

const ContentContainer = styled.div`
  height: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AnimatedContainer = styled(motion.div)`
  height: 100%;
  width: 100%;
  will-change: height;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;

  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
`;

const COMPOSER_FADE_HEIGHT_PX = 40;

const StyledSection = styled("section", {
  shouldForwardProp: prop => prop !== "$fadeColor",
})<{ $fadeColor?: string }>`
  width: 100%;
  padding-left: ${({ theme }) => `${theme.spacing[5]}`};
  padding-right: ${({ theme }) => `${theme.spacing[5]}`};
  padding-bottom: ${({ theme }) => `${theme.spacing[5]}`};
  padding-top: 8px;
  min-height: 60px;

  position: absolute;
  bottom: 0;

  ${({ $fadeColor }) =>
    $fadeColor &&
    `
    /* Opaque behind the composer so messages never show through it or the
       strip below it; the ::before gradient softens the upper boundary. */
    background: ${$fadeColor};

    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 100%;
      height: ${COMPOSER_FADE_HEIGHT_PX}px;
      background: linear-gradient(180deg, ${$fadeColor}00 0%, ${$fadeColor}DA 85.42%, ${$fadeColor} 100%);
      pointer-events: none;
    }
  `}
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
`;

export const StyledListContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
`;
